import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import { settingsApi } from '../api';
import { useToast } from '../context/ToastContext';

export default function ClinicSettings() {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => setForm(res.data))
      .catch((err) => toast.error(err));
  }, [toast]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsApi.update(form);
      toast.success('Settings saved. New prescriptions will use these details.');
    } catch (err) {
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  if (!form) return <Loader label="Loading settings..." className="py-5" />;

  return (
    <Form onSubmit={save}>
      <PageHeader title="Clinic Settings" subtitle="These details print on every prescription" icon="bi-gear-fill">
        <Button type="submit" className="btn-clinic" disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-check-lg me-2" />}
          Save Settings
        </Button>
      </PageHeader>

      <Row className="g-4">
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <i className="bi bi-hospital me-2" /> Clinic Identity
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Label className="required">Clinic Name</Form.Label>
                  <Form.Control required value={form.clinicName} onChange={(e) => set('clinicName', e.target.value)} />
                </Col>
                <Col xs={12}>
                  <Form.Label>Tagline</Form.Label>
                  <Form.Control
                    value={form.tagline}
                    onChange={(e) => set('tagline', e.target.value)}
                    placeholder="e.g. Family Health Care Centre"
                  />
                </Col>
                {/* <Col xs={12}>
                  <div className="env-panel">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <span className="fw-semibold small">
                        <i className="bi bi-shield-lock me-1" />
                        Doctor on the prescription
                      </span>
                      <span className="badge bg-light text-dark">set in .env</span>
                    </div>

                    <div className="fw-bold">{user.name}</div>
                    <div className="small text-muted">
                      {[user.qualification, user.specialization].filter(Boolean).join(' · ') || '-'}
                      {user.registrationNo ? ` · Reg. No: ${user.registrationNo}` : ''}
                    </div>

                    <div className="section-hint mt-2">
                      These come from the server's <code>.env</code> file, the same place the logins live, so
                      there is only one place to change them. Edit <code>DOCTOR_NAME</code>,{' '}
                      <code>DOCTOR_QUALIFICATION</code>, <code>DOCTOR_SPECIALIZATION</code> or{' '}
                      <code>DOCTOR_REG_NO</code> and restart the server.
                    </div>
                  </div>
                </Col> */}
 
                <Col md={6}>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control value={form.phone} onChange={(e) => set('phone', e.target.value)} />
                </Col>
                <Col md={6}>
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
                </Col>
                <Col xs={12}>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.address}
                    onChange={(e) => set('address', e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>Closed On</Form.Label>
                  <Form.Control
                    value={form.closedOn}
                    onChange={(e) => set('closedOn', e.target.value)}
                    placeholder="Sunday"
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label>Timings</Form.Label>
                  <Form.Control
                    value={form.timings}
                    onChange={(e) => set('timings', e.target.value)}
                    placeholder="Mon - Sat | 9:00 am - 1:00 pm, 5:00 pm - 8:00 pm"
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <i className="bi bi-printer me-2" /> Prescription Printing
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Paper Size</Form.Label>
                  <Form.Select value={form.paperSize} onChange={(e) => set('paperSize', e.target.value)}>
                    <option value="A4">A4</option>
                    <option value="A5">A5</option>
                  </Form.Select>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Form.Check
                    type="switch"
                    id="print-header"
                    label="Print the clinic header"
                    checked={form.printHeader}
                    onChange={(e) => set('printHeader', e.target.checked)}
                  />
                </Col>

                {!form.printHeader && (
                  <Col xs={12}>
                    <Form.Label>Blank space at the top (mm)</Form.Label>
                    <Form.Control
                      type="number"
                      value={form.headerTopSpaceMm}
                      onChange={(e) => set('headerTopSpaceMm', Number(e.target.value))}
                    />
                    <Form.Text className="text-muted">
                      Use this when you print on pre-printed letterhead paper, so nothing overlaps the printed header.
                    </Form.Text>
                  </Col>
                )}

                <Col xs={12}>
                  <Form.Label>Footer note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.footerNote}
                    onChange={(e) => set('footerNote', e.target.value)}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <i className="bi bi-cash-coin me-2" /> Fees
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Consultation Fee</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.defaultConsultationFee}
                    onChange={(e) => set('defaultConsultationFee', Number(e.target.value))}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Follow-up Fee</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.followUpFee}
                    onChange={(e) => set('followUpFee', Number(e.target.value))}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Follow-up window (days)</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.followUpFreeDays}
                    onChange={(e) => set('followUpFreeDays', Number(e.target.value))}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Text className="text-muted">
                    A patient returning within the follow-up window is charged the follow-up fee instead of the full
                    consultation fee. Reception can still change the amount on any visit.
                  </Form.Text>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Form>
  );
}
